import { AlarmClock, CalendarDays, Check, Repeat, Sparkle, type LucideIcon } from "lucide-react";
import type { Fassung, Praesenz } from "./einstellungen";
import { darf } from "./darf";
import type { OffenLage } from "./lage";
import { offenZellen, type OffenZelle } from "./saetze";
import { Zahl } from "./Zahl";
import { Zustand } from "./Zustand";

const SYMBOL: Record<OffenZelle["schluessel"], LucideIcon> = { ueberfaellig: AlarmClock, bald: CalendarDays, pruefen: Sparkle, fixkosten: Repeat };

/** Lesetext einer Zelle aus den sichtbaren Teilen: „Überfällig: 1, seit 13.10.“ (beide Fassungen gleich). */
function lesetext(z: OffenZelle): string {
  return `${z.name}: ${z.zahlText}, ${z.unter}`;
}

/** E12: Offen-Block aus einer `OffenLage`, zwei Darstellungen derselben Zellen, Texte und Ziele. Lebhaft vier
 *  Lichtkacheln 2 x 2 (Kreis 34 mit Symbol, Zahl 50 rechts oben, Name, Unterzeile; Licht nach `darf("lichtKachel")`,
 *  Zahl 30 % bis angestoßen, null in Mint mit Haken; die Zahlen zählen beim Auftritt hoch und ziehen danach nach). Ruhig
 *  eine leise Zählerleiste (Form neben der Zahl 24, Wort darunter; Wechsel als Blende, nie Zählen). Ein Tipp meldet die
 *  Zelle und ihr Element; das Ziel wählt die Seite (Überfällig und Bald: erste passende Zeile der Fristen, Zu prüfen:
 *  die Karte „Bitte prüfen“, Fixkosten: die Finanzen). */
export function OffenBlock(props: {
  lage: OffenLage;
  fassung: Fassung;
  praesenz: Praesenz;
  /** voller Auftritt: Zahlen zählen von 0 hoch (lebhaft, auch „Nur bei Erklärungen“, D-420) */
  hochzaehlen?: boolean;
  /** Zellen, die der Geist im Rundflug noch nicht angestoßen hat (Zahl 30 %) */
  dunkel?: ReadonlySet<string>;
  /** W82 Design-Web-1 (DWW-1): Zellen, in denen in der Eröffnung noch kein Brief gelandet ist; ihre Zahl steht auf 0 und zählt
   *  erst mit der Landung hoch */
  warten?: ReadonlySet<string>;
  onTipp: (zelle: OffenZelle["schluessel"], el: HTMLElement) => void;
}) {
  const zellen = offenZellen(props.lage);
  if (props.fassung === "ruhig") {
    return (
      <section className="offen-leiste glas" aria-label="Überblick" data-testid="offen-block" data-darstellung="leiste">
        {zellen.map((z) => (
          <button
            key={z.schluessel}
            type="button"
            className="offen-zelle"
            data-zelle={z.schluessel}
            data-null={z.zahl === 0 ? "" : undefined}
            aria-label={lesetext(z)}
            onClick={(e) => props.onTipp(z.schluessel, e.currentTarget)}
          >
            <span className="offen-zelle-kopf">
              <ZellenForm z={z} />
              {/* N3 (B10, R11 `.l-wert small`): „€“ klein (0,62 em), so bleibt „153 €“ bei 320 px und 200 % im Knopf */}
              <span className="offen-zahl w-offen-zahl">
                <Zahl wert={z.zahl} text={z.schluessel === "fixkosten" ? String(z.zahl) : z.zahlText} art="blende" />
                {z.schluessel === "fixkosten" && <small className="offen-einheit">{"\u202f€"}</small>}
              </span>
            </span>
            <span className="offen-wort w-offen-name" aria-hidden="true">
              {z.name}
            </span>
          </button>
        ))}
      </section>
    );
  }
  return (
    <section className="lichtkacheln" aria-label="Überblick" data-testid="offen-block" data-darstellung="kacheln">
      {zellen.map((z, i) => {
        const Symbol = SYMBOL[z.schluessel];
        const erklaert = (z.schluessel === "ueberfaellig" || z.schluessel === "pruefen") && z.zahl > 0;
        const licht = darf("lichtKachel", { fassung: props.fassung, praesenz: props.praesenz, erklaert });
        const null_ = z.zahl === 0 && z.schluessel !== "fixkosten";
        // N3 (B7): „€“ der Fixkosten klein neben der Zahl (0,55 em, R11 `.k-zahl small`); das schmale Leerzeichen hält den
        // Abstand und den Text „153 €“
        const euro = z.schluessel === "fixkosten";
        return (
          <button
            key={z.schluessel}
            type="button"
            className="lichtkachel glas"
            style={{ "--i": i } as React.CSSProperties}
            data-zelle={z.schluessel}
            data-zustand={null_ ? "null" : licht ? "an" : "ruht"}
            data-licht={licht ? "" : undefined}
            data-dunkel={props.dunkel?.has(z.schluessel) ? "" : undefined}
            aria-label={lesetext(z)}
            onClick={(e) => props.onTipp(z.schluessel, e.currentTarget)}
          >
            <span className="k-licht" aria-hidden="true" />
            <span className="k-kreis" aria-hidden="true">
              {null_ ? <Check size={18} strokeWidth={2} aria-hidden /> : <Symbol size={18} strokeWidth={1.75} aria-hidden />}
            </span>
            <span className="k-zahl w-kachel-zahl" aria-hidden="true">
              <Zahl wert={z.zahl} text={euro ? String(z.zahl) : z.zahlText} hochzaehlen={props.hochzaehlen} warten={props.warten?.has(z.schluessel)} format={euro ? (n) => String(Math.round(n)) : undefined} />
              {euro && <small className="k-einheit">{"\u202f€"}</small>}
            </span>
            <span className="k-name w-knopf-klein" aria-hidden="true">
              {z.name}
            </span>
            <span className="k-unter w-legende" aria-hidden="true">
              {z.unter}
            </span>
          </button>
        );
      })}
    </section>
  );
}

/** Form der ruhigen Zelle: Raute (überfällig), Punkt (bald), Funke 11, Wiederkehr 13; null wird Haken in Mint. */
function ZellenForm(props: { z: OffenZelle }) {
  const { z } = props;
  if (z.zahl === 0 && z.schluessel !== "fixkosten") return <Zustand form="haken" />;
  if (z.schluessel === "ueberfaellig") return <Zustand form="raute" />;
  if (z.schluessel === "bald") return <Zustand form="punkt" />;
  if (z.schluessel === "pruefen") return <Sparkle className="offen-form-funke" size={11} strokeWidth={2.5} aria-hidden />;
  return <Repeat className="offen-form-wiederkehr" size={13} strokeWidth={2.25} aria-hidden />;
}
